import { useEffect, useState } from "react";
import { TrendingDown, Zap, Loader2, Euro, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";

/**
 * Juni-2026 (User-Wunsch) — Schnellaktion für Ladenhüter (Standtage > 120T).
 *
 * Erlaubt dem Händler, direkt aus der Disposition heraus:
 *   1. den Verkaufspreis schnell zu senken (−5 % / −10 % / −15 % oder Zielpreis)
 *      → POST /api/dealer/disposition/{id}/reduce-price
 *   2. das Inserat zu boosten (Top-Inserat, kostet €Punkte)
 *      → POST /api/listings/{id}/boost  (Kosten via GET /api/listings/boost/info)
 *
 * Ziel: Standzeit-Problemfälle mit einem Klick angehen → Lagerumschlag steigern.
 */
export default function LadenhueterActionModal({ item, onClose, onDone }) {
    const price = Number(item.price || 0);
    const [customPrice, setCustomPrice] = useState("");
    const [reducing, setReducing] = useState(false);
    const [boostInfo, setBoostInfo] = useState(null);
    const [boosting, setBoosting] = useState(false);

    useEffect(() => {
        api.get("/listings/boost/info")
            .then((r) => setBoostInfo(r.data))
            .catch(() => setBoostInfo(null));
    }, []);

    const preview = (pct) => Math.round(price * (1 - pct / 100));

    const reducePrice = async (payload, label) => {
        setReducing(true);
        try {
            const r = await api.post(`/dealer/disposition/${item.id}/reduce-price`, payload);
            toast.success(`Preis gesenkt: ${formatPrice(r.data.old_price)} → ${formatPrice(r.data.new_price)} (${label})`);
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preis konnte nicht gesenkt werden");
        } finally {
            setReducing(false);
        }
    };

    const doBoost = async () => {
        setBoosting(true);
        try {
            await api.post(`/listings/${item.id}/boost`);
            toast.success("Inserat wurde als Top-Inserat geboostet 🚀");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Boost fehlgeschlagen");
        } finally {
            setBoosting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-md rounded-sm" data-testid="ladenhueter-action-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display font-black">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        Ladenhüter-Aktion
                    </DialogTitle>
                </DialogHeader>

                <div className="text-sm">
                    <div className="font-bold text-[#111827] truncate">{item.title || `${item.brand} ${item.model}`}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {item.standtage != null && <span className="text-red-600 font-bold">{item.standtage} Standtage</span>}
                        {price > 0 && <span> · aktuell {formatPrice(price)}</span>}
                    </div>
                </div>

                {/* Preis senken */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                        <TrendingDown className="w-4 h-4 text-emerald-600" /> Preis senken
                    </div>
                    {price > 0 ? (
                        <>
                            <div className="grid grid-cols-3 gap-2 mb-2">
                                {[5, 10, 15].map((pct) => (
                                    <Button
                                        key={pct}
                                        type="button"
                                        variant="outline"
                                        disabled={reducing}
                                        onClick={() => reducePrice({ percent: pct }, `−${pct} %`)}
                                        className="rounded-sm flex-col h-auto py-2"
                                        data-testid={`ladenhueter-reduce-${pct}`}
                                    >
                                        <span className="font-black text-base">−{pct} %</span>
                                        <span className="text-[10px] text-gray-500 font-mono-num">{formatPrice(preview(pct))}</span>
                                    </Button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                    <Euro className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        type="number"
                                        value={customPrice}
                                        onChange={(e) => setCustomPrice(e.target.value)}
                                        placeholder="Zielpreis"
                                        className="pl-7 h-9 rounded-sm"
                                        data-testid="ladenhueter-custom-price"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    disabled={reducing || !customPrice || Number(customPrice) <= 0 || Number(customPrice) >= price}
                                    onClick={() => reducePrice({ new_price: Number(customPrice) }, "Zielpreis")}
                                    className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                                    data-testid="ladenhueter-custom-apply"
                                >
                                    {reducing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Setzen"}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <p className="text-xs text-gray-500">Kein Verkaufspreis hinterlegt — bitte zuerst im Inserat pflegen.</p>
                    )}
                </div>

                {/* Boosten */}
                <div className="border border-gray-200 rounded-sm p-3">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                        <Zap className="w-4 h-4 text-[#FFCC00]" /> Boosten (Top-Inserat)
                    </div>
                    {boostInfo ? (
                        <>
                            <p className="text-xs text-gray-600 mb-2">
                                Top-Platzierung für <strong>{boostInfo.duration_days} Tage</strong> ·
                                Kosten: <strong className="font-mono-num">{boostInfo.cost_points} €Punkte</strong> ·
                                verfügbar: <span className="font-mono-num">{boostInfo.available_points}</span>
                            </p>
                            <Button
                                type="button"
                                disabled={boosting || !boostInfo.can_afford}
                                onClick={doBoost}
                                className="w-full rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black"
                                data-testid="ladenhueter-boost-btn"
                            >
                                {boosting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                    <>{boostInfo.can_afford ? `Jetzt boosten (${boostInfo.cost_points} €Punkte)` : "Nicht genug €Punkte"}</>
                                )}
                            </Button>
                        </>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Lade Boost-Konditionen …</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
